import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Radio, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BloqStacked } from "@/components/brand/BloqLogo";
import { WaitlistForm } from "@/components/lander/WaitlistForm";
import { useGated } from "@/hooks/usePublicConfig";
import { Button } from "@/components/ui/button";

function Shell({ children }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bloq-bg px-5 py-10 text-white">
      <div className="pointer-events-none absolute inset-0 grain opacity-50" />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-bloq-mint/10 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[360px] w-[360px] rounded-full bg-bloq-purple/12 blur-[140px]" />
      <div className="relative z-10 w-full max-w-md animate-fade-up">{children}</div>
    </div>
  );
}

export default function ChannelInvite() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { token, loading } = useAuth();
  const gated = useGated();

  // Authenticated visitors: resolve the handle → channel and deep-link into the app.
  const { data, isLoading } = useQuery({
    queryKey: ["channels"],
    queryFn: api.listChannels,
    enabled: !!token,
  });

  useEffect(() => {
    if (!token || isLoading || !data) return;
    const all = [...(data.mine || []), ...(data.discover || [])];
    const match = all.find((c) => c.handle?.toLowerCase() === handle?.toLowerCase());
    navigate(match ? `/app/channels/${match.id}` : "/app/channels", { replace: true });
  }, [token, isLoading, data, handle, navigate]);

  if (token) {
    return (
      <Shell>
        <div className="flex flex-col items-center text-center" data-testid="channel-invite-resolving">
          <Loader2 className="h-8 w-8 animate-spin text-bloq-mint" />
          <p className="mt-4 text-sm text-bloq-muted">Opening @{handle}…</p>
        </div>
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-bloq-mint" />
        </div>
      </Shell>
    );
  }

  // Non-authenticated visitors: growth funnel that adapts to launch state.
  return (
    <Shell>
      <Link to="/" className="mb-6 flex flex-col items-center" data-testid="channel-invite-home-link">
        <BloqStacked size={132} />
      </Link>
      <div
        className="rounded-[1.75rem] border border-white/10 bg-bloq-s1/80 p-7 shadow-glass backdrop-blur-xl sm:p-9"
        data-testid="channel-invite-card"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/10 bg-bloq-s2 text-bloq-blue">
            <Radio className="h-7 w-7" />
          </span>
          <div className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-bloq-blue">
            Verified broadcast
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold" data-testid="channel-invite-handle">
            @{handle}
          </h1>
          <p className="mt-2 text-sm text-bloq-muted">
            {gated
              ? "You've been invited to follow this channel on BLOQ Talk — the trust-gated messenger for Web3."
              : "Follow this channel on BLOQ Talk — the trust-gated messenger for Web3."}
          </p>
        </div>

        {gated ? (
          <div className="mt-7">
            <div
              className="mb-5 flex items-center justify-center gap-2 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-bloq-mint"
              data-testid="channel-invite-beta-badge"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Currently in private beta testing
            </div>
            <WaitlistForm showCounter={false} stacked buttonLabel="Join the waitlist" />
            <p className="mt-3 text-center text-xs text-bloq-muted">
              Get access to @{handle} and every channel when the private beta opens up.
            </p>
          </div>
        ) : (
          <div className="mt-7 space-y-3">
            <Button
              onClick={() => navigate("/signup")}
              className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
              data-testid="channel-invite-signup-btn"
            >
              Create your account <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="h-12 w-full rounded-full border-white/15 bg-transparent text-white hover:bg-white/5"
              data-testid="channel-invite-login-btn"
            >
              I already have an account
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
}
